import { useState } from "react";
import { useTranslation } from "react-i18next";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { LinkButton, type LinkButtonProps } from "~/common/components/Link";
import { requestConfirmation } from "~/common/utils/ModalService";
import ConfirmationModal from "~/common/components/ConfirmationModal";
import { CircularProgress, Portal, Typography } from "~/common/mui";
import {
  useAddProductsToComparisonMutation,
  useProductComparisonCount,
  useRemoveAllProductsFromComparisonMutation,
} from "~/features/compareProducts/api/productComparisonQuery";
import { ProductComparisonSnackbar } from "~/features/compareProducts/components/ProductComparisonCheckbox";

type Props = LinkButtonProps & {
  products: string[];
};

export const MMDCompareProductsButton = (props: Props) => {
  const { t } = useTranslation();
  const { count: comparisonCount } = useProductComparisonCount();
  const addProductsMutation = useAddProductsToComparisonMutation();
  const clearProductsMutation = useRemoveAllProductsFromComparisonMutation();
  const [loading, setLoading] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [baseMessage, setBaseMessage] = useState("");
  const [error, setError] = useState(false);

  const handleClick = async () => {
    const hasCompareProducts = comparisonCount > 0;
    if (hasCompareProducts) {
      const confirmed = await requestConfirmation(({ onConfirm, onCancel }) => {
        return (
          <ConfirmationModal
            okLabel={t("feature.mmd.assistant.compareProducts.modal.yes")}
            cancelLabel={t("cancel")}
            onOk={onConfirm}
            onCancel={onCancel}
          >
            <Typography variant="bodyLarge">{t("feature.mmd.assistant.compareProducts.modal.confirm")}</Typography>
          </ConfirmationModal>
        );
      });
      if (!confirmed) {
        return;
      }
    }
    setLoading(true);
    try {
      if (hasCompareProducts) {
        await clearProductsMutation.mutateAsync();
      }
      await addProductsMutation.mutateAsync(props.products);
      setBaseMessage("comparisonSnack.added");
      setError(false);
    } catch (error) {
      console.error(error);
      setBaseMessage(
        error instanceof Error && "code" in error && error.code === "LIST_FULL"
          ? "comparisonSnack.full"
          : "comparisonSnack.failedToAdd",
      );
      setError(true);
    } finally {
      setLoading(false);
      setShowSnackbar(true);
    }
  };

  return (
    <>
      <LinkButton
        {...props}
        iconBefore={loading ? <CircularProgress size={24} /> : <OpenInNewIcon />}
        disabled={loading}
        onClick={handleClick}
        data-test-id="mmd-compare-products-button"
      >
        {t("feature.mmd.assistant.compareProducts.button")}
      </LinkButton>
      <Portal container={document.body}>
        <ProductComparisonSnackbar
          open={showSnackbar}
          message={baseMessage}
          apiError={error}
          onClose={() => setShowSnackbar(false)}
        />
      </Portal>
    </>
  );
};
